import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt, FaTimes } from "react-icons/fa";
import "./CustomDatePicker.css";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useTranslation } from "react-i18next";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import Select from "react-select";
import { getCustomStyles } from "./helpers/customStyles";

interface ICustomDatePickerProps {
  startDate: Date | null;
  endDate?: Date;
  onChange: (dates: any) => void;
  onClear?: () => void;
  minDate?: Date;
  maxDate?: Date;
  placeholderText?: string;
  isRange: boolean;
  onCalendarClose?: (dates: any) => void;
  withPortal?: boolean;
}

const CustomDatePicker: React.FC<ICustomDatePickerProps> = ({
  startDate,
  endDate,
  onChange,
  onClear,
  minDate,
  maxDate,
  placeholderText = "From - To",
  isRange,
  onCalendarClose,
  withPortal = true,
}) => {
  const { t } = useTranslation();
  const darkTheme = localStorage.getItem("darkMode") === "true";
  const handleYesterdayClick = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    onChange([yesterday, yesterday]);
  };

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    label: new Date(0, i).toLocaleString("en", { month: "long" }),
    value: i,
  }));

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 100 }, (_, i) => {
    const year = currentYear - 50 + i;
    return { label: year.toString(), value: year };
  });

  return (
    <div className="relative">
      {isRange ? (
        <DatePicker
          selected={startDate}
          onChange={(dates) => onChange(dates as [Date | null, Date | null])}
          onCalendarClose={() => {
            if (onCalendarClose) onCalendarClose([startDate, endDate]);
          }}
          startDate={startDate}
          endDate={endDate}
          selectsRange={isRange}
          dateFormat="MM/dd/yyyy"
          minDate={minDate}
          maxDate={maxDate}
          placeholderText={
            placeholderText === "From - To"
              ? t("filtersSection.fromToPlaceholder")
              : placeholderText
          }
          className="border border-slate-300 dark:border-slate-700 rounded-lg h-10 px-3 pr-8 z-50 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm focus:outline-none range-datepicker"
          wrapperClassName="w-full"
          calendarClassName="custom-calendar ignore-outside-click"
          portalId="root-portal"
          popperPlacement="bottom-start"
          customInput={
            <input
              readOnly
              className="border border-slate-300 dark:border-slate-700 rounded-lg h-10 px-3 pr-8 z-50 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none range-datepicker w-full"
              value={
                startDate && endDate
                  ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                  : startDate
                    ? `${startDate.toLocaleDateString()}`
                    : ""
              }
            />
          }
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div className="flex items-center justify-between px-2 py-1">
              <button
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                className="text-gray-600 dark:text-gray-300 hover:text-del-green"
              >
                <IoIosArrowBack size={20} />
              </button>

              <div className="flex items-center">
                <Select
                  className="ignore-outside-click"
                  isSearchable={false}
                  options={monthOptions}
                  value={monthOptions.find(
                    (opt) => opt.value === date.getMonth(),
                  )}
                  onChange={(selectedOption) =>
                    selectedOption?.value !== undefined &&
                    changeMonth(selectedOption.value)
                  }
                  styles={getCustomStyles(darkTheme, true)}
                />

                <Select
                  className="ignore-outside-click"
                  isSearchable={false}
                  options={yearOptions}
                  value={yearOptions.find(
                    (opt) => opt.value === date.getFullYear(),
                  )}
                  onChange={(selectedOption) =>
                    selectedOption?.value !== undefined &&
                    changeYear(selectedOption.value)
                  }
                  styles={getCustomStyles(darkTheme, false)}
                />
              </div>

              <button
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                className="text-gray-600 dark:text-gray-300 hover:text-del-green"
              >
                <IoIosArrowForward size={20} />
              </button>
            </div>
          )}
        />
      ) : (
        <DatePicker
          withPortal={withPortal}
          selected={startDate}
          onChange={(date) => onChange(date as Date)}
          onCalendarClose={() => {
            if (onCalendarClose) onCalendarClose([startDate, endDate]);
          }}
          dateFormat="MM/dd/yyyy"
          className="w-full border border-slate-300 dark:border-slate-700 rounded-lg h-10 px-3 pr-10 text-sm z-50 text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-900 focus:outline-none"
          wrapperClassName="w-full"
          showPopperArrow={true}
          calendarClassName="custom-calendar"
          portalId="root-portal"
          popperPlacement="bottom-start"
        />
      )}

      {(startDate || endDate) && isRange ? (
        <FaTimes
          className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-300 cursor-pointer hover:text-del-red"
          onClick={onClear}
        />
      ) : (
        <FaCalendarAlt
          className={`absolute top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-300 pointer-events-none ${isRange ? "right-14" : "right-4"}`}
        />
      )}

      {isRange && (
        <div onClick={handleYesterdayClick}>
          <Tooltip
            anchorSelect={`.tooltip-anchor`}
            place="bottom"
            className="text-xs font-light"
            content={t("filtersSection.setYesterdaysDate")}
          />
          <svg
            stroke="currentColor"
            fill="currentColor"
            strokeWidth="0"
            viewBox="0 0 500 512"
            className="tooltip-anchor absolute right-0 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-300 cursor-pointer hover:text-blue-500 focus:outline-none"
            height="25px"
            width="25px"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#4b830d"
              d="M12 192h476c6.6 0 12 5.4 12 12v260c0 26.5-21.5 48-48 48H60c-26.5 0-48-21.5-48-48V204c0-6.6 5.4-12 12-12zm488-44v-36c0-26.5-21.5-48-48-48h-52V12c0-6.6-5.4-12-12-12h-44c-6.6 0-12 5.4-12 12v52H208V12c0-6.6-5.4-12-12-12h-44c-6.6 0-12 5.4-12 12v52H60c-26.5 0-48 21.5-48 48v36c0 6.6 5.4 12 12 12h476c6.6 0 12-5.4 12-12z"
            ></path>

            <path
              d="M40 356 L360 356"
              fill="none"
              stroke="#808080"
              strokeWidth="90"
            ></path>
            <path
              d="M40 356 L140 256"
              fill="none"
              stroke="#808080"
              strokeWidth="90"
              strokeLinecap="round"
            ></path>
            <path
              d="M40 356 L140 456"
              fill="none"
              stroke="#808080"
              strokeWidth="90"
              strokeLinecap="round"
            ></path>
            <path
              d="M380 356 L410 356"
              fill="none"
              stroke="#808080"
              strokeWidth="90"
            ></path>
            <path
              d="M430 356 L460 356"
              fill="none"
              stroke="#808080"
              strokeWidth="90"
            ></path>

            <path
              d="M40 356 L360 356"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="80"
            ></path>
            <path
              d="M40 356 L140 256"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="80"
              strokeLinecap="round"
            ></path>
            <path
              d="M40 356 L140 456"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="80"
              strokeLinecap="round"
            ></path>
            <path
              d="M380 356 L410 356"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="80"
            ></path>
            <path
              d="M430 356 L460 356"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="80"
            ></path>
          </svg>
        </div>
      )}
    </div>
  );
};

export { CustomDatePicker };
